/**
 * What the phone calendar shows, shaped from the lanes' own data. Pure.
 *
 * The phone is for the front desk asking "what is happening now" (the #2
 * user, 2026-10-30). It reads EXACTLY the items the laptop lanes read --
 * `useCalendarLaneData`, so the same three sources, the same automatic-
 * work exclusion (`isAutomaticTask`) and the same queue projection -- and
 * files them into robot groups with the lanes' own `buildBoard`, so an
 * unassigned item lands in the group its "Any robot" row would put it in
 * (`anyRobotGroupFor`). Nothing here decides what is on the calendar;
 * it only decides how a phone lays it out.
 *
 * The robot problems in "Needs a look" are the server's open alerts, read
 * with the attention list's own reader (`alertSubject`) and worded with
 * the hotel-word table the toasts use (`toastWordsFor`). No rule is
 * re-derived here.
 */
import { FIRE_MODE_HOLD_COPY, isFireModeError } from '../../../api/hooks/useSchedules';
import { isMaintenanceHold, MAINTENANCE_HOLD_COPY } from '../../../api/hooks/useMaintenance';
import type { TaskSchedule, ScheduleOccurrence } from '../../../api/v1';
import type { Audience } from '../../../auth/roles';
import { alertRule, alertSubject, type AttentionAlert } from '../../../state/attention/alert-subject';
import { toastWordsFor } from '../../../state/attention/toast-words';
import type { SiteTimeZone } from '../../../state/attention/words';
import { laneOfSchedule, type CalendarLane } from '../calendar-categories';
import { describeRule, isOneShotRule } from '../rrule';
import { locationFromRequest } from '../task-location';
import { DIDNT_FINISH, OUTCOME_LABEL, type LaneItem, type LaneOutcome } from './lane-items';
import {
  anyRobotText,
  buildBoard,
  unassignedText,
  type GroupDef,
  type RobotEntry,
} from './lane-layout';
import { dayText, planRoot, scheduleSpanText } from './schedule-span';
import { formatZonedClock, formatZonedDateTime } from '../zoned-time';
import {
  addDays,
  dateKeyOf,
  formatDuration,
  formatKey,
  weekdayOf,
  WEEKDAY_SHORT,
  type TimeWindow,
} from './lane-time';

const MIN = 60_000;

/** The console's phone tier (`AppShell.css`: the shell becomes a phone
 *  layout at and below 767px). */
export const PHONE_QUERY = '(max-width: 767px)';

export type PhoneTab = 'now' | 'today' | 'schedules';
export const PHONE_TABS: ReadonlyArray<{ id: PhoneTab; label: string }> = [
  { id: 'now', label: 'Now' },
  { id: 'today', label: 'Today' },
  { id: 'schedules', label: 'Schedules' },
];

/** `Fri 30/Oct/2026 · 13:30`: today and the time on the SITE's clock, the
 *  house date form. Beside the phone's "Tasks" title. */
export function phoneDateLine(nowMs: number, timeZone: string): string {
  const key = dateKeyOf(nowMs, timeZone);
  return `${WEEKDAY_SHORT[weekdayOf(key)]} ${formatKey(key)} · ${formatZonedClock(nowMs, timeZone)}`;
}

/** "Next 30 minutes". */
export const NEXT_WINDOW_MS = 30 * MIN;
/** Finished work folds into "Earlier today" as soon as it ends -- except
 *  work that DIDN'T finish, which stays in the list this long, where the
 *  front desk is looking. */
export const RECENT_MS = 30 * MIN;
/** Running cards shown before "+N more running". */
export const RUNNING_SHOWN = 4;
/** Next-30 rows shown before "+N more". */
export const NEXT_SHOWN = 8;
/** "Needs a look" lines shown before "+N more". */
export const NEEDS_LOOK_SHOWN = 4;
/** Two deliveries by one robot this close together are one burst. */
export const BURST_GAP_MS = 10 * MIN;

export function isRunning(o: LaneOutcome): boolean {
  return o === 'running' || o === 'attention';
}

export function isUpcoming(o: LaneOutcome): boolean {
  return o === 'scheduled' || o === 'queued';
}

export function isFinished(o: LaneOutcome): boolean {
  return (
    o === 'done' || o === 'failed' || o === 'cancelled' || o === 'skipped' || o === 'dispatched'
  );
}

/** A phone's status words: the lanes' labels, with "Running" for the
 *  plain running case (the card already says "now"). */
export function phoneStatus(o: LaneOutcome): string {
  return o === 'running' ? 'Running' : OUTCOME_LABEL[o];
}

// ---- groups -----------------------------------------------------------------

export interface GroupChip {
  id: string;
  name: string;
}

export interface GroupMembership {
  chips: GroupChip[];
  /** Item id -> the group it is filed under; absent = only under "All"
   *  (an unassigned item no group's "Any robot" row takes). */
  itemGroup: ReadonlyMap<string, string>;
  /** Robot key -> group. */
  robotGroup: ReadonlyMap<string, string>;
  /** Bare robot name -> group, for alerts (which name a robot, not a
   *  fleet). A name two groups share maps to neither. */
  nameGroup: ReadonlyMap<string, string>;
}

/**
 * Which group each item and robot belongs to, by the lanes' own filing
 * (`buildBoard`): a robot's items under its group, an unassigned item
 * under the group whose "Any robot" row would hold it, and robots no group
 * lists under "Other robots".
 */
export function groupMembership(
  items: readonly LaneItem[],
  groups: readonly GroupDef[],
  robots: readonly RobotEntry[],
): GroupMembership {
  const board = buildBoard(items, groups, robots);
  const itemGroup = new Map<string, string>();
  const robotGroup = new Map<string, string>();
  const nameGroup = new Map<string, string>();
  const clash = new Set<string>();
  const chips: GroupChip[] = [];
  for (const g of board.groups) {
    chips.push({ id: g.id, name: g.name });
    for (const row of g.rows) {
      for (const it of row.items) itemGroup.set(it.id, g.id);
      if (row.robot) {
        robotGroup.set(row.robot.key, g.id);
        const prev = nameGroup.get(row.robot.name);
        if (prev !== undefined && prev !== g.id) clash.add(row.robot.name);
        nameGroup.set(row.robot.name, g.id);
      }
    }
  }
  for (const n of clash) nameGroup.delete(n);
  return { chips, itemGroup, robotGroup, nameGroup };
}

/** The items a chip shows: everything for "All" (null). */
export function itemsInGroup(
  items: readonly LaneItem[],
  m: GroupMembership,
  groupId: string | null,
): LaneItem[] {
  if (groupId === null) return [...items];
  return items.filter((it) => m.itemGroup.get(it.id) === groupId);
}

export function robotsInGroup(
  robots: readonly RobotEntry[],
  m: GroupMembership,
  groupId: string | null,
): RobotEntry[] {
  if (groupId === null) return [...robots];
  return robots.filter((r) => m.robotGroup.get(r.key) === groupId);
}

// ---- words for one item -------------------------------------------------------

/** Where an item is, for a one-line row: its place, else the schedule's
 *  name without the type it would repeat beside the type pill. */
export function whereOf(item: LaneItem): string | null {
  if (item.place) return item.place;
  const head = `${item.typeLabel} · `;
  if (item.title.startsWith(head)) return item.title.slice(head.length);
  // A queued task RMF has not planned yet has no place, and its title is
  // only its type: say nothing rather than the type twice.
  return item.title === item.typeLabel ? null : item.title;
}

/** "Vacuum L17-05", or the type alone when the place is not known yet. */
export function whatOf(item: LaneItem): string {
  const where = whereOf(item);
  return where ? `${item.typeLabel} ${where}` : item.typeLabel;
}

/** Outcomes of work that is over: a task in one with no robot never got
 *  one. */
const ENDED: ReadonlySet<LaneOutcome> = new Set<LaneOutcome>(['done', 'failed', 'cancelled']);

/** The robot's words. With no robot (`lane-layout.ts`, one rule since
 *  2026-10-31): a run names the kind that will take it, "Any cleaning
 *  robot"; a task is "Not assigned yet". */
export function robotText(item: LaneItem, labelOf: (key: string | null) => string | null): string {
  return (
    labelOf(item.robotKey) ??
    item.robotName ??
    (item.kind === 'occurrence' ? anyRobotText(item.lane) : unassignedText(ENDED.has(item.outcome)))
  );
}

function finite(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}

/**
 * How far through a running task is, 0-100, or null when nobody gave an
 * estimate (then the end shown is only "not before now", and a bar would
 * be invented).
 */
export function progressOf(item: LaneItem, nowMs: number): number | null {
  if (!isRunning(item.outcome) || !item.live) return null;
  const t = item.live;
  if (!finite(t.estimatedRemainingSec) && !finite(t.estimatedDurationSec)) return null;
  const span = item.endMs - item.startMs;
  if (!(span > 0)) return null;
  return Math.max(0, Math.min(100, Math.round(((nowMs - item.startMs) / span) * 100)));
}

/** An item's length for a row: "12 min", "~12 min" for an estimate. */
export function lengthText(item: LaneItem): string {
  // A schedule has a start and no length: the lanes draw it 30 minutes
  // wide, but a phone row would read that as a fact.
  if (item.kind === 'occurrence' && item.outcome === 'scheduled') return '';
  const ms = item.endMs - item.startMs;
  if (!(ms > 0)) return '—';
  return `${item.estimated ? '~' : ''}${formatDuration(ms)}`;
}

// ---- Now ------------------------------------------------------------------------

export interface CalendarIssue {
  id: string;
  /** The robot (or "Robot job"), bold on the row. */
  subject: string;
  words: string;
  sinceMs: number | null;
  /** The group the robot is filed under, when it can be told. */
  groupId: string | null;
}

/** The alert rules about a robot whose work is on the calendar: a robot
 *  reporting a problem (NAV_STALLED, BOARDING_BLOCKED, ...), one not
 *  moving on a job, one in error or not reporting, a job stuck. */
export const CALENDAR_ISSUE_RULES: ReadonlySet<string> = new Set([
  'robot_issue',
  'robot_stalled',
  'robot_error',
  'robot_unreachable',
  'task_attention',
]);

function robotOfSubject(a: AttentionAlert): string | null {
  const s = alertSubject(a);
  if (!s) return null;
  if ('robot' in s) return s.robot ?? null;
  return null;
}

/**
 * Open robot alerts, in the reader's words, one per alert. Words come from
 * `toastWordsFor` with the row's own clock left out (the row shows the
 * time on its right), and a sentence that opens with the robot's name
 * loses it (the name is already bold beside it).
 */
export function calendarIssues(
  alerts: readonly AttentionAlert[],
  opts: {
    audience: Audience;
    clock: SiteTimeZone;
    wallNowMs: number;
    nameGroup: ReadonlyMap<string, string>;
  },
): CalendarIssue[] {
  const out: CalendarIssue[] = [];
  for (const a of alerts) {
    const rule = alertRule(a);
    if (!rule || !CALENDAR_ISSUE_RULES.has(rule)) continue;
    const robot = robotOfSubject(a);
    if (rule === 'task_attention' && !robot) continue;
    const w = toastWordsFor({ ...a, openedAtMs: undefined }, opts.audience, opts.clock, opts.wallNowMs);
    const subject = w.source || robot || 'Robot';
    let words = w.message.trim();
    if (words.startsWith(`${subject} `)) words = words.slice(subject.length + 1);
    words = words.replace(/\.$/, '');
    out.push({
      id: a.id,
      subject,
      words: words.charAt(0).toUpperCase() + words.slice(1),
      sinceMs: finite(a.openedAtMs) ? a.openedAtMs : null,
      groupId: robot ? (opts.nameGroup.get(robot) ?? null) : null,
    });
  }
  return out.sort((x, y) => (y.sinceMs ?? 0) - (x.sinceMs ?? 0));
}

export interface NowModel {
  /** Today's work that did not finish, newest first. */
  didntFinish: LaneItem[];
  /** Running now, soonest to end first. */
  running: LaneItem[];
  /** Robots in view doing no calendar work and able to take some. */
  freeRobots: number;
  /** Starting in the next 30 minutes (scheduled, or queued behind a
   *  robot's current job), earliest first. */
  next: LaneItem[];
}

export function buildNowModel(args: {
  items: readonly LaneItem[];
  robots: readonly RobotEntry[];
  /** Robot keys the live list says cannot take work (offline, in error,
   *  waiting to be re-localised). */
  unavailable: ReadonlySet<string>;
  nowMs: number;
  day: TimeWindow;
}): NowModel {
  const { items, nowMs, day } = args;
  const didntFinish = items
    .filter(
      (it) =>
        DIDNT_FINISH.has(it.outcome) &&
        it.endMs <= Math.max(nowMs, it.startMs) &&
        it.endMs >= day.fromMs &&
        it.startMs < day.toMs,
    )
    .sort((a, b) => b.endMs - a.endMs || a.id.localeCompare(b.id));
  const running = items
    .filter((it) => isRunning(it.outcome))
    .sort((a, b) => a.endMs - b.endMs || a.id.localeCompare(b.id));
  const busy = new Set<string>();
  for (const it of items) {
    if (it.robotKey && (isRunning(it.outcome) || it.outcome === 'paused')) busy.add(it.robotKey);
  }
  const freeRobots = args.robots.filter(
    (r) => !busy.has(r.key) && !args.unavailable.has(r.key),
  ).length;
  const next = items
    .filter(
      (it) => isUpcoming(it.outcome) && it.startMs >= nowMs && it.startMs < nowMs + NEXT_WINDOW_MS,
    )
    .sort((a, b) => a.startMs - b.startMs || a.id.localeCompare(b.id));
  return { didntFinish, running, freeRobots, next };
}

// ---- Today -----------------------------------------------------------------------

export type AgendaRow =
  | { kind: 'item'; id: string; item: LaneItem }
  | {
      kind: 'burst';
      id: string;
      items: LaneItem[];
      startMs: number;
      endMs: number;
    };

export interface AgendaModel {
  /** Finished work, in order (a recent failure stays out of it). */
  earlier: AgendaRow[];
  earlierCount: { done: number; didntFinish: number; other: number };
  rows: AgendaRow[];
  /** Where the now line goes: before `rows[nowIndex]` (= rows.length
   *  when everything left started already). */
  nowIndex: number;
}

function byStart(a: LaneItem, b: LaneItem): number {
  return a.startMs - b.startMs || a.endMs - b.endMs || a.id.localeCompare(b.id);
}

type BurstPhase = 'finished' | 'upcoming';

function burstPhase(o: LaneOutcome): BurstPhase | null {
  if (isFinished(o)) return 'finished';
  if (isUpcoming(o)) return 'upcoming';
  return null;
}

/**
 * Rows for an ordered list, with each robot's runs of deliveries folded
 * into one row ("3 deliveries"): a front desk sending ten room drops an
 * hour wants one line per round, not ten. A run is deliveries by one
 * robot, each starting within `BURST_GAP_MS` of the last one's end, all
 * finished or all still to come. Running work is never folded.
 */
export function withBursts(sorted: readonly LaneItem[]): AgendaRow[] {
  const chains: LaneItem[][] = [];
  const open = new Map<string, LaneItem[]>();
  for (const it of sorted) {
    const phase = burstPhase(it.outcome);
    if (it.lane !== 'delivery' || !it.robotKey || phase === null) continue;
    const key = `${it.robotKey}|${phase}`;
    const chain = open.get(key);
    const last = chain?.[chain.length - 1];
    if (chain && last && it.startMs - last.endMs <= BURST_GAP_MS) {
      chain.push(it);
    } else {
      const fresh = [it];
      chains.push(fresh);
      open.set(key, fresh);
    }
  }
  const burstOf = new Map<string, LaneItem[]>();
  for (const c of chains) if (c.length >= 2) for (const it of c) burstOf.set(it.id, c);

  const rows: AgendaRow[] = [];
  for (const it of sorted) {
    const chain = burstOf.get(it.id);
    if (!chain) {
      rows.push({ kind: 'item', id: it.id, item: it });
      continue;
    }
    if (chain[0]!.id !== it.id) continue;
    rows.push({
      kind: 'burst',
      id: `burst:${it.id}`,
      items: chain,
      startMs: chain[0]!.startMs,
      endMs: Math.max(...chain.map((c) => c.endMs)),
    });
  }
  return rows;
}

export function rowStartMs(r: AgendaRow): number {
  return r.kind === 'item' ? r.item.startMs : r.startMs;
}

export function buildAgenda(items: readonly LaneItem[], nowMs: number): AgendaModel {
  const earlierItems: LaneItem[] = [];
  const restItems: LaneItem[] = [];
  for (const it of [...items].sort(byStart)) {
    const recentFailure = DIDNT_FINISH.has(it.outcome) && it.endMs >= nowMs - RECENT_MS;
    if (isFinished(it.outcome) && it.endMs <= nowMs && !recentFailure) earlierItems.push(it);
    else restItems.push(it);
  }
  const earlierCount = { done: 0, didntFinish: 0, other: 0 };
  for (const it of earlierItems) {
    if (it.outcome === 'done') earlierCount.done += 1;
    else if (DIDNT_FINISH.has(it.outcome)) earlierCount.didntFinish += 1;
    else earlierCount.other += 1;
  }
  const rows = withBursts(restItems);
  let nowIndex = rows.findIndex((r) => rowStartMs(r) >= nowMs);
  if (nowIndex === -1) nowIndex = rows.length;
  return { earlier: withBursts(earlierItems), earlierCount, rows, nowIndex };
}

/** A burst's words: "Rooms 0611, 1209, 1610", its length range and how
 *  it went ("Done", or "9 done" beside "1 didn’t finish"). */
export function burstWords(items: readonly LaneItem[]): {
  where: string;
  length: string;
  status: string;
  /** How many did not finish (shown apart, in the fail colour). */
  didntFinish: number;
  allFailed: boolean;
} {
  const places = items.map((it) => whereOf(it)).filter((p): p is string => p !== null);
  const where =
    places.length === 0
      ? 'Places not known yet'
      : places.length <= 3
        ? places.join(', ')
        : `${places.slice(0, 3).join(', ')} +${places.length - 3}`;
  const lens = items.map((it) => it.endMs - it.startMs).filter((ms) => ms > 0);
  const lo = lens.length ? Math.min(...lens) : 0;
  const hi = lens.length ? Math.max(...lens) : 0;
  const mins = (ms: number) => Math.max(1, Math.round(ms / MIN));
  const length = !lens.length ? '' : mins(lo) === mins(hi) ? `${mins(lo)} min` : `${mins(lo)}–${mins(hi)} min`;
  const didntFinish = items.filter((it) => DIDNT_FINISH.has(it.outcome)).length;
  const outcomes = new Set(items.map((it) => it.outcome));
  const allFailed = didntFinish === items.length;
  const status = allFailed
    ? outcomes.size === 1
      ? phoneStatus(items[0]!.outcome)
      : phoneStatus('failed')
    : didntFinish > 0
      ? `${items.length - didntFinish} done`
      : phoneStatus(items[0]!.outcome);
  return { where, length, status, didntFinish, allFailed };
}

// ---- Schedules -------------------------------------------------------------------

/** The series the Schedules tab lists -- the old Month sidebar's rule: one-off
 *  bookings are left out unless paused (a paused one is listed nowhere
 *  else, and must stay reachable).
 *
 *  And a series that has ENDED is left out, paused or not, when `nowMs`
 *  is given: its end date (`until_ms`) has passed, so it will never run
 *  again and there is nothing to do with it from a list of what runs.
 *  Its past runs stay on the calendar, where the past is read
 *  (2026-10-30). */
export function isListedSeries(
  s: Pick<TaskSchedule, 'enabled' | 'rrule'> & { until_ms?: number | null },
  nowMs?: number,
): boolean {
  if (nowMs !== undefined && hasEnded(s, nowMs)) return false;
  return !s.enabled || !isOneShotRule(s.rrule);
}

/** Its end date has passed: it will never run again. */
export function hasEnded(s: { until_ms?: number | null }, nowMs: number): boolean {
  return s.until_ms !== null && s.until_ms !== undefined && s.until_ms < nowMs;
}

export interface ScheduleRow {
  schedule: TaskSchedule;
  lane: CalendarLane;
  /** The rule in words, with its end ("…, until 03/Nov/2026"). */
  rule: string;
  /** The rule without its end date, for a card or a panel that says
   *  the end as `span` beside it (`patternBeside` in a line). */
  pattern: string;
  /** "From Wed 04/Nov", "Until Tue 03/Nov", both, or null
   *  (`scheduleSpanText`): what tells two schedules of one name apart --
   *  a carry-over and the schedule it came from. */
  span: string | null;
  robots: string;
  /** Where it goes ("L16-05 +4"), from its request; null when unknown. */
  places: string | null;
  /** The next run still to come in the loaded window, if any. */
  next: ScheduleOccurrence | null;
  active: boolean;
  /** Its end date has passed (only a row built with `listAll` can be). */
  ended: boolean;
}

/** The schedule's robot, or the kind that will take a run when none is
 *  pinned: "Any cleaning robot" (`anyRobotText`). The phone said "Any
 *  robot" and the laptop Agenda "Any cleaning robot" until 2026-10-31. */
export function scheduleRobotText(
  s: TaskSchedule,
  labelOf: (key: string | null) => string | null,
  lane: CalendarLane = laneOfSchedule(s),
): string {
  const robot = (s.task?.robot ?? '').trim();
  const fleet = (s.task?.fleet ?? '').trim();
  if (robot) return labelOf(`${fleet}/${robot}`) ?? robot;
  return anyRobotText(lane);
}

export function buildScheduleRows(args: {
  schedules: readonly TaskSchedule[];
  occurrences: readonly ScheduleOccurrence[];
  nowMs: number;
  timeZone: string;
  labelOf: (key: string | null) => string | null;
  /** The laptop Agenda's order: active first by next run, paused last.
   *  The phone lists paused ones first (they are listed nowhere else on
   *  a phone). */
  agenda?: boolean;
  /** Every schedule given, listed or not -- for a panel already open on
   *  one that has just ended or been made a one-off. */
  listAll?: boolean;
}): ScheduleRow[] {
  const nextOf = new Map<string, ScheduleOccurrence>();
  for (const o of args.occurrences) {
    // Not fired yet, and not already past (the runner fires within a
    // tick, so a minute's grace keeps "now" from flickering away).
    if (o.status || o.start_ms < args.nowMs - MIN) continue;
    const prev = nextOf.get(o.schedule_id);
    if (!prev || o.start_ms < prev.start_ms) nextOf.set(o.schedule_id, o);
  }
  const listed = args.listAll
    ? args.schedules
    : args.schedules.filter((s) => isListedSeries(s, args.nowMs));
  const rows = listed.map((s) => {
    const lane = laneOfSchedule(s);
    // Without the zone when it is the site's: both calendars already
    // say every time on them is the site's.
    const zone = s.timezone || args.timeZone;
    const opts = { siteTimeZone: args.timeZone };
    const span = scheduleSpanText(s, args.nowMs, args.timeZone);
    return {
      schedule: s,
      lane,
      rule: describeRule(s.rrule, s.dtstart_ms, s.until_ms, zone, opts),
      pattern: describeRule(s.rrule, s.dtstart_ms, null, zone, opts),
      span,
      robots: scheduleRobotText(s, args.labelOf, lane),
      places: locationFromRequest(s.category, s.task)?.text ?? null,
      next: s.enabled ? (nextOf.get(s.schedule_id) ?? null) : null,
      active: s.enabled,
      ended: hasEnded(s, args.nowMs),
    };
  });
  const pausedAfter = args.agenda ? 1 : -1;
  // The rows of one plan -- the old time an edit kept, and the schedule
  // it came from (`planRoot`) -- sit together, the earlier first, where
  // the first of them sorts: the one running now has a next run, the
  // other may have none in the days read, and apart they read as two
  // schedules. Two made separately under one name are two groups.
  const groupOf = (r: ScheduleRow) => `${r.active}:${planRoot(r.schedule.schedule_id)}`;
  const groupNext = new Map<string, number>();
  for (const r of rows) {
    const next = r.next?.start_ms ?? Infinity;
    groupNext.set(groupOf(r), Math.min(groupNext.get(groupOf(r)) ?? Infinity, next));
  }
  return rows.sort((a, b) => {
    if (a.active !== b.active) return a.active ? -pausedAfter : pausedAfter;
    const an = groupNext.get(groupOf(a))!;
    const bn = groupNext.get(groupOf(b))!;
    if (an !== bn) return an - bn;
    return (
      a.schedule.name.localeCompare(b.schedule.name) ||
      groupOf(a).localeCompare(groupOf(b)) ||
      a.schedule.dtstart_ms - b.schedule.dtstart_ms
    );
  });
}

/** "Today 14:00", "Tomorrow 08:00", else the house date and time. */
export function nextRunText(ms: number, nowMs: number, timeZone: string): string {
  const today = dateKeyOf(nowMs, timeZone);
  const key = dateKeyOf(ms, timeZone);
  if (key === today) return `Today ${formatZonedClock(ms, timeZone)}`;
  if (key === addDays(today, 1)) return `Tomorrow ${formatZonedClock(ms, timeZone)}`;
  return formatZonedDateTime(ms, timeZone);
}

/**
 * A run as a question names it: "today's 09:00 run", "tomorrow's 09:00
 * run", else "the 09:00 run on Tue 03/Nov". `nextRunText` is a list
 * entry's words, and dropped into "Skip the … run?" it read "Skip the
 * Tomorrow 09:00 run?" (2026-10-31).
 */
export function runNamed(ms: number, nowMs: number, timeZone: string): string {
  const today = dateKeyOf(nowMs, timeZone);
  const key = dateKeyOf(ms, timeZone);
  const clock = formatZonedClock(ms, timeZone);
  if (key === today) return `today’s ${clock} run`;
  if (key === addDays(today, 1)) return `tomorrow’s ${clock} run`;
  return `the ${clock} run on ${dayText(ms, timeZone, nowMs)}`;
}

export function scheduleMatches(row: ScheduleRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const s = row.schedule;
  return [s.name, row.rule, row.robots, row.places ?? '', s.task?.description ?? '']
    .some((t) => t.toLowerCase().includes(q));
}

// ---- what an action came to ---------------------------------------------------

/**
 * A refused schedule action in words. Fire mode and a maintenance window
 * are HOLDS, said as such (the same copy the dispatch form uses): the
 * person keeps the right, nothing was sent, and it is not their fault.
 */
export function actionErrorWords(err: unknown): { message: string; tone: 'hold' | 'error' } {
  if (isFireModeError(err)) return { message: FIRE_MODE_HOLD_COPY, tone: 'hold' };
  if (isMaintenanceHold(err)) return { message: MAINTENANCE_HOLD_COPY, tone: 'hold' };
  const msg = err instanceof Error ? err.message : String(err ?? 'Not done.');
  return { message: `Not done: ${msg}`, tone: 'error' };
}
